import { useCallback, useReducer, useRef, useEffect } from 'react';

const initialState = {
  retryCount: 0,
  isActive: false,
  delay: 0,
};

const ACTIONS = {
  START: 'START_TIMER',
  RESET: 'RESET_TIMER',
  TIMER_COMPLETE: 'TIMER_COMPLETE',
};

const reducer = (state, { type, payload }) => {
  switch (type) {
    case ACTIONS.START:
      return {
        ...state,
        isActive: true,
        retryCount: state.retryCount + 1,
        delay: payload.delay,
      };
    case ACTIONS.RESET:
      return { ...state, retryCount: 0, isActive: false, delay: 0 };
    case ACTIONS.TIMER_COMPLETE:
      return { ...state, isActive: false };
    default:
      return state;
  }
};

/**
 * Hook to manage a timer with exponential backoff and jitter
 * @returns {object} - Backoff management interface
 */
const useBackoff = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const timerRef = useRef(null);

  // Clear timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  /**
   * Calculates delay with exponential backoff and jitter
   * @param {number} attempt - Current retry attempt number
   * @param {number} baseDelay - Base delay in milliseconds
   * @param {number} maxDelay - Maximum delay in milliseconds
   * @returns {number} - Calculated delay in milliseconds
   */
  const calculateDelay = useCallback((attempt, baseDelay = 1000, maxDelay = 30000) => {
    // Calculate exponential backoff
    const exponentialDelay = Math.min(maxDelay, baseDelay * 2 ** attempt);

    // Add jitter - random value between 0 and 30% of the calculated delay
    const jitter = Math.random() * 0.3 * exponentialDelay;

    return Math.floor(exponentialDelay + jitter);
  }, []);

  const startTimer = useCallback(() => {
    const newDelay = calculateDelay(state.retryCount);

    dispatch({
      type: ACTIONS.START,
      payload: {
        delay: newDelay,
      },
    });

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      dispatch({
        type: ACTIONS.TIMER_COMPLETE,
        payload: {
          isActive: false,
        },
      });
    }, newDelay);

    return newDelay;
  }, [calculateDelay, state.retryCount]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    dispatch({
      type: ACTIONS.RESET,
    });
  }, []);

  return {
    isActive: state.isActive,
    delay: state.delay,
    retryCount: state.retryCount,
    startTimer,
    resetTimer,
  };
};

export default useBackoff;
